/* eslint-disable max-lines */
import { EventLoggerInstanceName } from '~/shared/constants/commonConstants'
import {
  PaymentMethod,
  PaymentSystem,
} from '~/shared/constants/paymentConstants'
import { getCurrentOnboardingVariant } from '~/shared/helpers/getCurrentOnboardingVariant'
import { getMobileOperatingSystem } from '~/shared/helpers/getMobileOperatingSystem'
import {
  IEvent,
  IEventLogger,
  TQuestionPageParams,
} from '~/shared/types/eventsTypes'
import { TProductId } from '~/shared/types/subscriptionsTypes'

export const enum ScreenName {
  ONBOARDING = 'onboarding',
  UPSELL_OFFER = 'upsell_offer',
  CANCEL_OFFER = 'cancel_offer',
}

export const enum Source {
  INITIAL = 'initial',
  CANCEL_OFFER = 'cancel_offer',
  EMAIL = 'email',
}

export const enum Events {
  SESSION_STARTED = 'session_start',
  FIRST_PAGE_COMPLETED = 'first_page_completed',
  QUESTION_COMPLETED = 'question_page_completed',
  FIRST_PAGE_SCROLLED = 'first_page_scrolled',
  FIRST_PAGE_BUTTON_TAP = 'first_page_button_tap',
  FILES_SCANNING_COMPLETED = 'files_scanning_completed',
  EMAIL_PAGE_SHOW = 'email_page_show',
  EMAIL_PAGE_COMPLETED = 'email_page_completed',
  EMAIL_PAGE_ERROR = 'email_page_error',
  TERMS_OF_USE = 'terms_of_use_tap',
  PRIVACY_POLICY = 'privacy_policy_tap',
  PAYMENT_METHOD_SELECTED = 'payment_method_selected',
  SUBS_PURCHASE_SCROLLED = 'subs_purchase_scrolled',
  PURCHASE_SHOW = 'subs_purchase_show',
  PURCHASE_SCREEN_CLOSE = 'subs_purchase_screen_close',
  CANCEL_OFFER_PAGE_SHOW = 'cancel_offer_page_show',
  CANCEL_OFFER_PAGE_CLOSE = 'cancel_offer_page_close',
  PURCHASE_STARTED = 'subs_purchase_started',
  PURCHASE_COMPLETED = 'subs_purchase_completed',
  PURCHASE_FAILED = 'subs_purchase_failed',
  FINISH_ACCOUNT_SCREEN_VIEW = 'finish_account_screen_view',
  DOWNLOAD_BTN_PRESSED = 'download_button_press',
  UPSELL_PURCHASE_SHOW = 'upsell_purchase_show',
  UPSELL_PURCHASE_CLOSE = 'upsell_purchase_close',
  AB_SEGMENT = 'ab_segment',
  PURCHASE_STARTED_POPUP = 'subs_purchase_started_popup',
  PURCHASE_STARTED_PAYPAL = 'subs_purchase_started_paypal',
  SALE_SCREEN_SHOW = 'plans_page_show',
  EMAIL_EXISTS_POPUP_SHOW = 'email_exists_popup_show',
  EMAIL_EXISTS_POPUP_CLOSE = 'email_exists_popup_close',
  CHECK_YOUR_INBOX_SCREEN_SHOW = 'check_your_inbox_screen_show',
  CHECK_YOUR_INBOX_CONTINUE_TAP = 'check_your_inbox_continue_tap',
  BI_USER_AGE = 'bi_user_age',
  BI_USER_GENDER = 'bi_user_gender',
  BI_USER_GOAL = 'bi_user_goal',
  CREATE_ACCOUNT_PAGE = 'create_account_page',
  ACCOUNT_CREATED = 'account_created',
  ACCOUNT_CREATION_FAILED = 'account_creation_failed',
  SCRATCH_DISCOUNT_PAGE_SHOW = 'scratch_discount_page_show',
  SCRATCHING_STARTED = 'scratching_started',
  SCRATCHING_FINISHED = 'scratching_finished',
  PLANS_PAGE_BUTTON_TAP = 'plans_page_button_tap',
  GENERATION_PLAN_SCREEN_SHOW = 'generation_plan_screen_show',
  GENERATION_PLAN_QUESTION_COMPLETED = 'generation_plan_question_completed',
  PRIMER_TOKENIZE_ERROR = 'primer_tokenize_error',
  PRIMER_HANDLER_SUCCESS_FAILED = 'primer_handler_success_failed',
  PAYMENT_DECLINED_SHOWN = 'payment_declined_shown',
  PRIMER_INIT_ERROR = 'primer_init_error',
}

class EventLogger {
  private loggers?: Map<EventLoggerInstanceName, IEventLogger>
  private eventsQueue: IEvent[] = []
  private eventsCache: Map<Events, string> = new Map()

  get isAmplitudeActive() {
    return this.loggers?.has(EventLoggerInstanceName.AMPLITUDE)
  }

  get getIsGiaActive() {
    return this.loggers?.has(EventLoggerInstanceName.GIA)
  }

  init(...loggers: IEventLogger[]): void {
    const entriesArr = loggers.map(
      (logger) =>
        [logger.name, logger] as [EventLoggerInstanceName, IEventLogger],
    )

    if (!this.loggers) {
      this.loggers = new Map(entriesArr)
      this.notifyInitFinished()
      return
    }

    if (this.loggers) {
      loggers.map((logger) =>
        this.loggers?.set(
          logger.name as EventLoggerInstanceName,
          logger as IEventLogger,
        ),
      )
    }

    this.notifyInitFinished()
  }

  logSessionStarted = (): void => {
    this.logEventOrPushToQueue({
      event: Events.SESSION_STARTED,
      eventProperty: {
        device_type: getMobileOperatingSystem(),
      },
      isAmplitudeEvent: false,
    })
  }

  logFirstPageCompleted(question?: string): void {
    this.logEventOrPushToQueue({
      event: Events.FIRST_PAGE_COMPLETED,
      eventProperty: question ? { question } : undefined,
    })
  }

  logQuestion({ question, answers, pageName }: TQuestionPageParams): void {
    this.logEventOrPushToQueue({
      event: Events.QUESTION_COMPLETED,
      eventProperty: {
        question,
        answer: Array.isArray(answers) ? answers.join(',') : answers,
        screen_name: pageName,
      },
    })
  }

  logGenerationPlanScreenShow(): void {
    this.logEventOrPushToQueue({ event: Events.GENERATION_PLAN_SCREEN_SHOW })
  }

  logGenerationPlanQuestionCompleted(question: string, answer: string): void {
    this.logEventOrPushToQueue({
      event: Events.GENERATION_PLAN_QUESTION_COMPLETED,
      eventProperty: { question, answer },
    })
  }

  logBiUserAge(ageBucket: string, skipAgeSplit = false): void {
    const startAge = parseInt(ageBucket, 10)
    const age35Split = startAge >= 35 ? '35+' : '<35'

    this.logEventOrPushToQueue({
      event: Events.BI_USER_AGE,
      eventProperty: {
        answer: ageBucket,
        ...(!skipAgeSplit && { age_35_split: age35Split }),
      },
    })
  }

  logBiUserGender(answer: string): void {
    this.logEventOrPushToQueue({
      event: Events.BI_USER_GENDER,
      eventProperty: { answer },
    })
  }

  logBiUserGoal(answer: string[]): void {
    this.logEventOrPushToQueue({
      event: Events.BI_USER_GOAL,
      eventProperty: { answer },
    })
  }

  logScratchDiscountPageShow(): void {
    this.logEventOrPushToQueue({
      event: Events.SCRATCH_DISCOUNT_PAGE_SHOW,
    })
  }

  logScratchingStarted(): void {
    this.logEventOrPushToQueue({
      event: Events.SCRATCHING_STARTED,
    })
  }

  logScratchingFinished(attemptNumber: number): void {
    this.logEventOrPushToQueue({
      event: Events.SCRATCHING_FINISHED,
      eventProperty: { attempt_number: attemptNumber },
    })
  }

  logCreateAccountPageShown(): void {
    this.logEventOrPushToQueue({
      event: Events.CREATE_ACCOUNT_PAGE,
    })
  }

  logAccountCreated(email: string): void {
    this.logEventOrPushToQueue({
      event: Events.ACCOUNT_CREATED,
      eventProperty: {
        method: 'email',
        email,
      },
    })
  }

  logAccountCreationFailed({
    email,
    errorReason,
  }: {
    email: string
    errorReason: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.ACCOUNT_CREATION_FAILED,
      eventProperty: {
        error_reason: errorReason,
        email,
      },
    })
  }

  logFirstPageButtonTapped({
    buttonNumber,
    buttonText,
  }: {
    buttonNumber: string
    buttonText: string
  }) {
    this.logEventOrPushToQueue({
      event: Events.FIRST_PAGE_BUTTON_TAP,
      eventProperty: { button_number: buttonNumber, button_text: buttonText },
    })
  }

  logPageScrolled({ value, event }: { value: number; event: Events }) {
    this.logEventOrPushToQueue({
      event,
      eventProperty: { scrolled_part: `${value}%` },
    })
  }

  logFilesScanningCompleted() {
    this.logEventOrPushToQueue({
      event: Events.FILES_SCANNING_COMPLETED,
    })
  }

  // EmailWrapper Page Events
  logEmailPageShown(): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_PAGE_SHOW,
    })
  }

  logEmailPageCompleted(eventProperty: { email: string }): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_PAGE_COMPLETED,
      eventProperty,
    })
  }

  logEmailPageError(eventProperty: { error: string }): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_PAGE_ERROR,
      eventProperty,
    })
  }

  logTermsOfUseClicked(): void {
    this.logEventOrPushToQueue({ event: Events.TERMS_OF_USE })
  }

  logPrivacyPolicyClicked(): void {
    this.logEventOrPushToQueue({ event: Events.PRIVACY_POLICY })
  }

  logCancelOfferPageShown(): void {
    this.logEventOrPushToQueue({ event: Events.CANCEL_OFFER_PAGE_SHOW })
  }

  logCancelOfferPageClosed(): void {
    this.logEventOrPushToQueue({ event: Events.CANCEL_OFFER_PAGE_CLOSE })
  }

  logPurchaseScreenClose({
    screenName,
    productId,
  }: {
    screenName: ScreenName
    productId?: TProductId
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_SCREEN_CLOSE,
      eventProperty: {
        screen_name: screenName,
        ...(productId && { product_id: productId }),
      },
    })
  }

  logPaymentMethodSelected(paymentMethod: PaymentMethod) {
    const lastLoggedPaymentMethod = this.eventsCache.get(
      Events.PAYMENT_METHOD_SELECTED,
    )

    if (lastLoggedPaymentMethod === paymentMethod) return

    this.eventsCache.set(Events.PAYMENT_METHOD_SELECTED, paymentMethod)
    this.logEventOrPushToQueue({
      event: Events.PAYMENT_METHOD_SELECTED,
      eventProperty: {
        payment_method: paymentMethod,
      },
    })
  }

  logSalePageShown({
    productIds,
    screenName = ScreenName.ONBOARDING,
  }: {
    productIds: string[]
    screenName?: ScreenName
  }): void {
    const event = Events.SALE_SCREEN_SHOW
    const eventProperty = {
      product_id: productIds.join(','),
      screen_name: screenName,
    }

    this.logEventOrPushToQueue({
      event,
      eventProperty,
    })
  }

  logPlansPageButtonTap({
    buttonNumber,
    buttonText,
    screenName,
  }: {
    buttonNumber: number
    buttonText: string
    screenName: ScreenName
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PLANS_PAGE_BUTTON_TAP,
      eventProperty: {
        button_number: buttonNumber,
        button_text: buttonText,
        screen_name: screenName,
      },
    })
  }

  logPurchaseShown({
    screenName,
    stripeAccountId,
    stripeAccountName,
    productId,
    source = Source.INITIAL,
  }: {
    screenName: ScreenName
    stripeAccountId: string
    stripeAccountName: string
    productId?: TProductId
    source?: Source
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_SHOW,
      eventProperty: {
        onboarding_id: '',
        screen_name: screenName,
        stripe_account_name: stripeAccountName,
        stripe_account_id: stripeAccountId,
        source,
        ...(productId && { product_id: productId }),
      },
    })
  }

  logPurchaseStartedPayPal({
    productId,
    priceDetails: { price, trial = false, currency },
    screenName,
    paymentSystem,
    stripeAccountName,
    stripeAccountId,
  }: {
    productId: TProductId
    priceDetails: {
      price: number
      trial?: boolean
      currency: string
    }
    screenName: ScreenName
    paymentSystem: PaymentSystem
    stripeAccountName?: string
    stripeAccountId?: string
  }): void {
    const event = Events.PURCHASE_STARTED_PAYPAL
    const eventProperty = {
      trial,
      price,
      currency,
      product_id: productId,
      payment_method: PaymentMethod.PAYPAL,
      payment_system: paymentSystem,
      ...(stripeAccountName && { stripe_account_name: stripeAccountName }),
      ...(stripeAccountId && { stripe_account_id: stripeAccountId }),
      screen_name: screenName,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logPurchaseStarted({
    productId,
    productKey,
    priceDetails: { price, trial = false, currency = 'USD' },
    paymentMethod,
    email,
    source = Source.INITIAL,
    screenName,
    paymentSystem,
    stripeAccountId,
    stripeAccountName,
    isForce3DS,
    paymentProcessor,
  }: {
    productId: TProductId
    productKey?: string
    priceDetails: { price: number; trial?: boolean; currency?: string }
    paymentMethod: PaymentMethod
    email: string
    source?: Source
    screenName: ScreenName
    paymentSystem: PaymentSystem
    stripeAccountId?: string
    stripeAccountName?: string
    isForce3DS: boolean
    paymentProcessor?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_STARTED,
      eventProperty: {
        email,
        source,
        onboarding_id: '',
        trial,
        price,
        currency,
        product_id: productId,
        ...(productKey && { product_key: productKey }),
        screen_name: screenName,
        payment_system: paymentSystem,
        ...(stripeAccountName && { stripe_account_name: stripeAccountName }),
        ...(stripeAccountId && { stripe_account_id: stripeAccountId }),
        payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
        '3ds_force': isForce3DS,
        ...(paymentProcessor && { payment_processor: paymentProcessor }),
      },
    })
  }

  logPurchaseStartedPopup({
    productId,
    productKey,
    priceDetails: { price, trial = false, currency = 'USD' },
    paymentMethod,
    email,
    source = Source.INITIAL,
    screenName,
    paymentSystem,
    stripeAccountId,
    stripeAccountName,
    isForce3DS,
    paymentProcessor,
  }: {
    productId: TProductId
    productKey?: string
    priceDetails: { price: number; trial?: boolean; currency?: string }
    paymentMethod: PaymentMethod
    email: string
    source?: Source
    screenName: ScreenName
    paymentSystem: PaymentSystem
    stripeAccountId?: string
    stripeAccountName?: string
    isForce3DS: boolean
    paymentProcessor?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_STARTED_POPUP,
      eventProperty: {
        email,
        source,
        onboarding_id: '',
        trial,
        price,
        currency,
        product_id: productId,
        ...(productKey && { product_key: productKey }),
        screen_name: screenName,
        payment_system: paymentSystem,
        ...(stripeAccountName && { stripe_account_name: stripeAccountName }),
        ...(stripeAccountId && { stripe_account_id: stripeAccountId }),
        payment_method: paymentMethod,
        '3ds_force': isForce3DS,
        ...(paymentProcessor && { payment_processor: paymentProcessor }),
      },
    })
  }

  logPurchaseCompleted({
    productId,
    productKey,
    priceDetails: { price, trial = false, currency = 'USD' },
    paymentMethod,
    paymentSystem,
    discountApplied,
    transactionId,
    email,
    source = Source.INITIAL,
    screenName,
    stripeAccountName,
    stripeAccountId,
    isForce3DS,
    paymentProcessor,
  }: {
    productId: TProductId
    productKey?: string
    priceDetails: { price: number; trial?: boolean; currency?: string }
    email: string
    paymentMethod?: PaymentMethod
    paymentSystem: PaymentSystem
    discountApplied?: string
    transactionId?: string
    source?: Source
    screenName: ScreenName
    stripeAccountName?: string
    stripeAccountId?: string
    isForce3DS: boolean
    paymentProcessor?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_COMPLETED,
      eventProperty: {
        email,
        onboarding_id: '',
        source,
        trial,
        price,
        currency,
        product_id: productId,
        ...(productKey && { product_key: productKey }),
        screen_name: screenName,
        payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
        payment_system: paymentSystem,
        ...(transactionId && { transaction_id: transactionId }),
        ...(stripeAccountName && { stripe_account_name: stripeAccountName }),
        ...(stripeAccountId && { stripe_account_id: stripeAccountId }),
        ...(discountApplied && { discount_applied: discountApplied }),
        '3ds_force': isForce3DS,
        ...(paymentProcessor && { payment_processor: paymentProcessor }),
      },
    })
  }

  logPurchaseFailed({
    productId,
    productKey,
    priceDetails: { price, trial = false, currency = 'USD' },
    error: { description, type, code },
    paymentMethod,
    paymentSystem,
    stripeAccountId,
    stripeAccountName,
    source = Source.INITIAL,
    email,
    screenName,
    isForce3DS,
    paymentProcessor,
  }: {
    productId: TProductId
    productKey?: string
    priceDetails: { price: number; trial?: boolean; currency?: string }
    error: { type: string; description?: string; code?: string }
    paymentMethod?: PaymentMethod
    paymentSystem: PaymentSystem
    stripeAccountId?: string
    stripeAccountName?: string
    source?: Source
    email: string
    screenName: ScreenName
    isForce3DS: boolean
    paymentProcessor?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_FAILED,
      eventProperty: {
        email,
        onboarding_id: '',
        source,
        trial,
        price,
        currency,
        error_type: type,
        payment_system: paymentSystem,
        ...(stripeAccountName && { stripe_account_name: stripeAccountName }),
        ...(stripeAccountId && { stripe_account_id: stripeAccountId }),
        ...(description && { error_description: description }),
        ...(code && { error_code: code }),
        product_id: productId,
        ...(productKey && { product_key: productKey }),
        screen_name: screenName,
        payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
        '3ds_force': isForce3DS,
        ...(paymentProcessor && { payment_processor: paymentProcessor }),
      },
    })
  }

  // Getting App Page Events
  logGettingAppShown(): void {
    this.logEventOrPushToQueue({ event: Events.FINISH_ACCOUNT_SCREEN_VIEW })
  }

  logDownloadClicked(callback: () => void): void {
    this.logEventOrPushToQueue({
      event: Events.DOWNLOAD_BTN_PRESSED,
      callback,
    })
  }

  logUpsellPurchaseShown(): void {
    this.logEventOrPushToQueue({
      event: Events.UPSELL_PURCHASE_SHOW,
      eventProperty: {
        screen_name: ScreenName.UPSELL_OFFER,
      },
    })
  }

  logUpsellPurchaseClose(): void {
    this.logEventOrPushToQueue({
      event: Events.UPSELL_PURCHASE_CLOSE,
      eventProperty: {
        screen_name: ScreenName.UPSELL_OFFER,
      },
    })
  }

  logGrowthBookAbSegmentName({
    variantId,
    experimentKey,
    variantName,
  }: {
    experimentKey: string
    variantId: string
    variantName: string
  }): void {
    const event = Events.AB_SEGMENT
    const eventProperty = {
      growthbook_feature_key: experimentKey || 'experiment_id_not_set',
      ab_variant: getCurrentOnboardingVariant(variantId),
      ab_segment_name: variantName || 'no_name',
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logEmailExistsPopupShow(email: string): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_EXISTS_POPUP_SHOW,
      eventProperty: { email },
    })
  }

  logEmailExistsPopupClose(email: string, tap: string): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_EXISTS_POPUP_CLOSE,
      eventProperty: { email, tap },
    })
  }

  logCheckYourInboxScreenShow(): void {
    this.logEventOrPushToQueue({
      event: Events.CHECK_YOUR_INBOX_SCREEN_SHOW,
    })
  }

  logCheckYourInboxContinueTap(): void {
    this.logEventOrPushToQueue({
      event: Events.CHECK_YOUR_INBOX_CONTINUE_TAP,
    })
  }

  logPrimerTokenizeError(code?: string, message?: string): void {
    this.logEventOrPushToQueue({
      event: Events.PRIMER_TOKENIZE_ERROR,
      eventProperty: { code, message },
    })
  }

  logPrimerHandlerSuccessFailed(error: string): void {
    this.logEventOrPushToQueue({
      event: Events.PRIMER_HANDLER_SUCCESS_FAILED,
      eventProperty: { error },
    })
  }

  logPaymentDeclined(source: string): void {
    this.logEventOrPushToQueue({
      event: Events.PAYMENT_DECLINED_SHOWN,
      eventProperty: { source },
    })
  }

  logPrimerInitError(error: unknown): void {
    const message =
      error instanceof Error ? error.message : JSON.stringify(error)
    this.logEventOrPushToQueue({
      event: Events.PRIMER_INIT_ERROR,
      eventProperty: { message },
    })
  }

  private logEventOrPushToQueue({
    event,
    eventProperty,
    isAmplitudeEvent = true,
    callback,
  }: {
    event: Events
    eventProperty?: Record<string, any>
    isAmplitudeEvent?: boolean
    callback?: () => void
  }): void {
    if (this.getIsGiaActive) {
      this.logEvent({
        event,
        eventProperty,
        isAmplitudeEvent,
        callback,
      })
    } else {
      this.eventsQueue.push({
        event,
        eventProperty,
        isAmplitudeEvent,
        callback,
      })
    }
  }

  private notifyInitFinished() {
    if (this.eventsQueue.length) {
      this.eventsQueue.forEach(
        ({ event, eventProperty, isAmplitudeEvent, callback }) =>
          this.logEvent({
            event,
            eventProperty,
            isAmplitudeEvent,
            callback,
          }),
      )
      this.eventsQueue = []
    }
  }

  private logEvent({
    event,
    eventProperty,
    isAmplitudeEvent = true,
    callback,
  }: {
    event: Events
    eventProperty?: Record<string, any>
    isAmplitudeEvent?: boolean
    callback?: () => void
  }): void {
    if (!isAmplitudeEvent) {
      this.loggers
        ?.get(EventLoggerInstanceName.GIA)
        ?.log(event, eventProperty, callback)

      this.loggers
        ?.get(EventLoggerInstanceName.USER_FLOW_TRACKER)
        ?.log(event, eventProperty, callback)
      return
    }

    if (this.loggers) {
      const eventLoggers = [...this.loggers.values()]
      eventLoggers.forEach((logger, index, array) => {
        index === array.length - 1
          ? logger.log(event, eventProperty, callback)
          : logger.log(event, eventProperty)
      })
    }
  }
}

export const eventLogger = new EventLogger()
